
import { PatternStyle } from "@/data/caseStudyTypes";

// Get pattern styles based on case study ID and industry
export const getPatternStyles = (id?: string, industry?: string): PatternStyle => {
  // Default pattern if no ID or industry
  if (!id && !industry) return { 
    primary: "#6501E5", // edgefi Purple
    secondary: "#C4F63A", // Chartreuse
    rotation: 10,
    shapes: [
      { type: "circle", cx: 100, cy: 150, r: 40, fill: "rgba(196, 246, 58, 0.2)" },
      { type: "circle", cx: 300, cy: 250, r: 70, fill: "rgba(101, 1, 229, 0.1)" },
      { type: "path", d: "M150,50 L250,150 L150,250 Z", fill: "rgba(149, 115, 208, 0.15)" }
    ]
  };
  
  // Industry-specific patterns
  const industryPatterns: Record<string, PatternStyle> = {
    "Financial Services": { 
      primary: "#6501E5", // edgefi Purple
      secondary: "#A4B6E2", // Powder Blue
      rotation: 5,
      shapes: [
        // Graph/chart-like elements for financial services
        { type: "path", d: "M50,250 L100,200 L150,220 L200,180 L250,150 L300,180 L350,120", fill: "none", stroke: "rgba(196, 246, 58, 0.4)", strokeWidth: 3 },
        { type: "rect", x: 50, y: 230, width: 30, height: 20, fill: "rgba(101, 1, 229, 0.15)" },
        { type: "rect", x: 100, y: 180, width: 30, height: 40, fill: "rgba(101, 1, 229, 0.15)" },
        { type: "rect", x: 150, y: 200, width: 30, height: 20, fill: "rgba(101, 1, 229, 0.15)" },
        { type: "rect", x: 200, y: 160, width: 30, height: 40, fill: "rgba(101, 1, 229, 0.15)" },
        { type: "rect", x: 250, y: 130, width: 30, height: 40, fill: "rgba(101, 1, 229, 0.15)" },
        { type: "rect", x: 300, y: 160, width: 30, height: 40, fill: "rgba(101, 1, 229, 0.15)" },
        { type: "circle", cx: 300, cy: 100, r: 30, fill: "rgba(164, 182, 226, 0.2)" }, // Dollar-like symbol
      ]
    },
    "Healthcare": { 
      primary: "#9573D0", // Amethyst
      secondary: "#A4B6E2", // Powder Blue
      rotation: -5,
      shapes: [
        // Healthcare/medical-inspired elements
        { type: "path", d: "M150,100 L150,200 M100,150 L200,150", stroke: "rgba(196, 246, 58, 0.3)", strokeWidth: 8, fill: "none" }, // Cross symbol
        { type: "circle", cx: 260, cy: 150, r: 50, fill: "rgba(164, 182, 226, 0.15)" }, // Circular medical symbol
        { type: "path", d: "M230,150 C230,120 290,120 290,150 C290,180 230,180 230,150", fill: "rgba(101, 1, 229, 0.1)" }, // Wave pattern (heartbeat)
        { type: "path", d: "M80,230 C100,200 120,260 140,230 C160,200 180,260 200,230", stroke: "rgba(149, 115, 208, 0.2)", strokeWidth: 3, fill: "none" } // EKG line
      ]
    },
    "Hospitality": {
      primary: "#C4F63A", // Chartreuse
      secondary: "#9573D0", // Amethyst
      rotation: 8,
      shapes: [
        // Restaurant/hospitality elements
        { type: "path", d: "M120,100 L280,100 L280,200 L120,200 Z", fill: "rgba(149, 115, 208, 0.1)" }, // Table
        { type: "circle", cx: 150, cy: 150, r: 20, fill: "rgba(164, 182, 226, 0.15)" }, // Plate
        { type: "circle", cx: 250, cy: 150, r: 20, fill: "rgba(164, 182, 226, 0.15)" }, // Plate
        { type: "path", d: "M100,220 L300,220 L280,250 L120,250 Z", fill: "rgba(101, 1, 229, 0.1)" }, // Service counter
        { type: "path", d: "M200,80 L220,80 L220,120 L200,120 Z", fill: "rgba(196, 246, 58, 0.2)" } // Condiment
      ]
    },
    "Technology": {
      primary: "#6501E5", // edgefi Purple
      secondary: "#C4F63A", // Chartreuse
      rotation: 0,
      shapes: [
        // Circuit board and technology patterns
        { type: "path", d: "M100,100 L300,100 L300,200 L100,200 Z", fill: "none", stroke: "rgba(196, 246, 58, 0.2)", strokeWidth: 2 }, // Chip outline
        { type: "path", d: "M100,150 L150,150 M250,150 L300,150 M200,100 L200,150", stroke: "rgba(164, 182, 226, 0.3)", strokeWidth: 3, fill: "none" }, // Circuit traces
        { type: "circle", cx: 150, cy: 150, r: 10, fill: "rgba(101, 1, 229, 0.2)" }, // Circuit node
        { type: "circle", cx: 250, cy: 150, r: 10, fill: "rgba(101, 1, 229, 0.2)" }, // Circuit node
        { type: "circle", cx: 200, cy: 150, r: 10, fill: "rgba(149, 115, 208, 0.2)" }, // Circuit node
        { type: "path", d: "M130,180 L270,180 L270,230 L130,230 Z", fill: "rgba(196, 246, 58, 0.1)" } // Processor/chip
      ]
    },
    "Recreation & Tourism": {
      primary: "#A4B6E2", // Powder Blue
      secondary: "#C4F63A", // Chartreuse
      rotation: -12,
      shapes: [
        // Mountain/recreation-themed elements
        { type: "path", d: "M50,250 L150,100 L250,180 L350,80", fill: "none", stroke: "rgba(196, 246, 58, 0.3)", strokeWidth: 3 }, // Mountain outline
        { type: "path", d: "M50,250 L150,100 L250,180 L350,80 L350,250 L50,250", fill: "rgba(164, 182, 226, 0.1)" }, // Mountain fill
        { type: "circle", cx: 150, cy: 80, r: 20, fill: "rgba(149, 115, 208, 0.15)" }, // Sun
        { type: "path", d: "M190,150 L210,150 L210,180 L190,180 Z", fill: "rgba(101, 1, 229, 0.15)" }, // Lodge/cabin
        { type: "path", d: "M180,150 L220,150 L200,130 Z", fill: "rgba(101, 1, 229, 0.15)" } // Lodge/cabin roof
      ]
    },
    "Healthcare Technology": {
      primary: "#9573D0", // Amethyst
      secondary: "#6501E5", // edgefi Purple
      rotation: 5,
      shapes: [
        // Combination of healthcare and technology elements
        { type: "path", d: "M150,100 L150,200 M100,150 L200,150", stroke: "rgba(196, 246, 58, 0.2)", strokeWidth: 5, fill: "none" }, // Medical cross
        { type: "path", d: "M240,100 L300,100 L300,200 L240,200 Z", fill: "none", stroke: "rgba(164, 182, 226, 0.2)", strokeWidth: 2 }, // Tech component
        { type: "path", d: "M100,230 C120,200 140,260 160,230 C180,200 200,260 220,230", stroke: "rgba(101, 1, 229, 0.15)", strokeWidth: 2, fill: "none" }, // Heartbeat/data line
        { type: "circle", cx: 270, cy: 150, r: 30, fill: "rgba(149, 115, 208, 0.1)" }, // Tech circle
        { type: "path", d: "M250,130 L290,130 L290,170 L250,170 Z", fill: "rgba(164, 182, 226, 0.1)" } // Data chip
      ]
    },
    "Cybersecurity": {
      primary: "#6501E5", // edgefi Purple
      secondary: "#C4F63A", // Chartreuse
      rotation: 5,
      shapes: [
        // Shield and security-themed elements
        { type: "path", d: "M150,70 L250,70 L250,170 Q200,220 150,170 Z", fill: "rgba(101, 1, 229, 0.1)", stroke: "rgba(196, 246, 58, 0.2)", strokeWidth: 2 }, // Shield shape
        { type: "circle", cx: 200, cy: 120, r: 25, fill: "rgba(149, 115, 208, 0.1)" }, // Lock circle
        { type: "path", d: "M190,140 L210,140 L210,110 L190,110 Z", fill: "rgba(196, 246, 58, 0.15)" }, // Lock body
        { type: "path", d: "M50,200 L350,200", stroke: "rgba(164, 182, 226, 0.2)", strokeWidth: 2, strokeDasharray: "10,5" }, // Network line
        { type: "circle", cx: 100, cy: 200, r: 8, fill: "rgba(101, 1, 229, 0.2)" }, // Network node
        { type: "circle", cx: 200, cy: 200, r: 8, fill: "rgba(101, 1, 229, 0.2)" }, // Network node
        { type: "circle", cx: 300, cy: 200, r: 8, fill: "rgba(101, 1, 229, 0.2)" } // Network node
      ]
    },
    "IT Services": {
      primary: "#9573D0", // Amethyst
      secondary: "#A4B6E2", // Powder Blue
      rotation: -3,
      shapes: [
        // IT support and infrastructure elements
        { type: "rect", x: 130, y: 100, width: 140, height: 90, rx: 5, fill: "rgba(149, 115, 208, 0.1)" }, // Monitor/screen
        { type: "path", d: "M180,190 L220,190 L210,220 L190,220 Z", fill: "rgba(101, 1, 229, 0.15)" }, // Monitor stand
        { type: "rect", x: 160, y: 230, width: 80, height: 20, rx: 5, fill: "rgba(196, 246, 58, 0.1)" }, // Keyboard
        { type: "path", d: "M100,140 C50,180 50,100 100,140", stroke: "rgba(164, 182, 226, 0.3)", strokeWidth: 2, fill: "none" }, // Cloud shape left
        { type: "path", d: "M300,140 C350,180 350,100 300,140", stroke: "rgba(164, 182, 226, 0.3)", strokeWidth: 2, fill: "none" }, // Cloud shape right
        { type: "path", d: "M100,140 L300,140", stroke: "rgba(196, 246, 58, 0.2)", strokeWidth: 2, strokeDasharray: "5,5" } // Connection line
      ]
    }
  };
  
  // Case study specific overrides (can be extended)
  const caseStudyPatterns: Record<string, PatternStyle> = {
    "financial-services-m365": { 
      ...industryPatterns["Financial Services"],
      rotation: 15
    },
    "healthcare-strategic-security": {
      ...industryPatterns["Healthcare"],
      rotation: -5
    },
    "restaurant-chain-m365": {
      ...industryPatterns["Hospitality"],
      rotation: 8
    },
    "oregon-ski-resort": {
      ...industryPatterns["Recreation & Tourism"],
      rotation: -8
    },
    "tech-startup": {
      ...industryPatterns["Technology"],
      rotation: 5
    },
    "biomedtech-security-transformation": {
      ...industryPatterns["Healthcare Technology"],
      rotation: -3
    }
  };
  
  // First try to get a case study specific pattern
  if (id && caseStudyPatterns[id]) {
    return caseStudyPatterns[id];
  }
  
  // Then try to get an industry-specific pattern
  if (industry && industryPatterns[industry]) {
    return industryPatterns[industry];
  }
  
  // Use a default pattern if no other matches
  return { 
    primary: "#6501E5", // edgefi Purple
    secondary: "#C4F63A", // Chartreuse
    rotation: 10,
    shapes: [
      { type: "circle", cx: 100, cy: 150, r: 40, fill: "rgba(196, 246, 58, 0.2)" },
      { type: "circle", cx: 300, cy: 250, r: 70, fill: "rgba(101, 1, 229, 0.1)" },
      { type: "path", d: "M150,50 L250,150 L150,250 Z", fill: "rgba(149, 115, 208, 0.15)" }
    ]
  };
};
